/* eslint-disable  */
import React, { useState, useEffect } from 'react';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import GenericInput from '@ui-components/Input/GenericInput';
import GenericButton from '@ui-components/Button/GenericButton';
//import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import CustomRadioGroup, { CustomRadioOption } from "@ui-components/RadioGroup/CustomRadioGroup";
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { createBgpNeighbor, updateBgpNeighbor } from '../../request/request';

interface InterfaceAddress {
  id: number;
  ipAddress: string;
}

interface Interface {
  id: number;
  interface: string;
  macAddress: string;
  isPrimary: boolean;
  tags: string;
  discoveredAt: string;
  interfaceAddresses: InterfaceAddress[];
}

const TABS: TabOption[] = [
  { label: 'Basic', value: 'Basic' },
  { label: 'Operating mode', value: 'Operating mode' },
  { label: 'Timers', value: 'Timers' },
  { label: 'Advanced', value: 'Advanced' },
];

interface BgpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  mode?: "add" | "edit";
  initialValues?: any;
  reflectorId?: number;
  getAccessTokenSilently: () => Promise<string>;
  context?: 'reflector' | 'cluster';
  clusterId?: number;
  clusterReflectors?: number[];
  clusterInterfaceIds?: number[];
}

const OPERATING_MODE_OPTIONS: CustomRadioOption[] = [
  {
    value: "enterprise",
    label: "Entreprise Mode",
    description: "Optimized for enterprise networks with simplified routing policies and automatic next-hop resolution",
  },
  {
    value: "telco",
    label: "Telco Mode",
    description: "Advanced features for service providers networks, including MPLS support, route distinguishers, and complex policy options.",
  },
];

const BgpModal: React.FC<BgpModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "add",
  initialValues = {},
  reflectorId,
  getAccessTokenSilently,
  context,
  clusterId,
  clusterReflectors,
  clusterInterfaceIds,
}) => {
  const [activeTab, setActiveTab] = useState<string>('Basic');
  const [interfaces, setInterfaces] = useState<Interface[]>([]);
  const [selectedInterfaceId, setSelectedInterfaceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    variant: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    description: "",
    variant: "success"
  });

  // Form fields for Basic tab
  const [neighborAddress, setNeighborAddress] = useState('');
  const [localAddress, setLocalAddress] = useState('');
  const [peerAs, setPeerAs] = useState('');
  const [localAs, setLocalAs] = useState('');
  const [description, setDescription] = useState('');
  const [operatingMode, setOperatingMode] = useState<'enterprise' | 'telco'>('enterprise');
  const [holdTime, setHoldTime] = useState('90');
  const [keepaliveInterval, setKeepaliveInterval] = useState('30');
  const [connectRetry, setConnectRetry] = useState('120');
  const [enableGracefulRestart, setEnableGracefulRestart] = useState(true);
  const [enableAddPath, setEnableAddPath] = useState(false);
  const [enableBfd, setEnableBfd] = useState(true);
  const [bfdInterval, setBfdInterval] = useState('300');
  const [bfdMultiplier, setBfdMultiplier] = useState('3');
  const [md5Password, setMd5Password] = useState('');

  // Cluster
  // const [interfaces1, setInterfaces1] = useState<Interface[]>([]);
  // const [interfaces2, setInterfaces2] = useState<Interface[]>([]);
  // const [selectedInterfaceId1, setSelectedInterfaceId1] = useState<number | null>(null);
  // const [selectedInterfaceId2, setSelectedInterfaceId2] = useState<number | null>(null);
  // const [localAddress1, setLocalAddress1] = useState('');
  // const [localAddress2, setLocalAddress2] = useState('');

  // Add validation check
  const isFormValid = () => {
    const commonValidation = 
      neighborAddress.trim() !== '' &&
      peerAs.trim() !== '' &&
      localAs.toString().trim() !== '' &&
      holdTime.trim() !== '' &&
      keepaliveInterval.trim() !== '' &&
      connectRetry.trim() !== '' &&
      bfdInterval.trim() !== '' &&
      bfdMultiplier.trim() !== '' &&
      (operatingMode === 'enterprise' || operatingMode === 'telco');
    
    // For cluster mode, check both local addresses - COMMENTED OUT FOR NOW
    /* if (context === 'cluster') {
      const isValid = commonValidation && 
        (mode === "edit" || (localAddress1.trim() !== '' && localAddress2.trim() !== ''));
      
      return isValid;
    }
    
    // For standalone mode
    const isValid = commonValidation && 
      (mode === "edit" || localAddress.trim() !== '');
    
    return isValid; */
    
    // For now, just return common validation since local addresses are not required
    return commonValidation;
  };

  // Fetch interfaces - COMMENTED OUT FOR NOW since we're not using local addresses
  /* useEffect(() => {
    if (!isOpen) return; // Don't fetch if modal is not open
    
    if (context === 'cluster' && clusterReflectors && clusterReflectors.length === 2) {
      // Fetch for both reflectors
      const fetchBoth = async () => {
        try {
          const token = await getAccessTokenSilently();
          const [res1, res2] = await Promise.all([
            getReflectorInterfaces(token, clusterReflectors[0]),
            getReflectorInterfaces(token, clusterReflectors[1]),
          ]);
          setInterfaces1(res1.data);
          setInterfaces2(res2.data);
        } catch (err) {
          setError('Failed to load interfaces');
        }
      };
      fetchBoth();
    } else if (reflectorId) {
      // Standalone
      const fetchSingle = async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await getReflectorInterfaces(token, reflectorId);
          setInterfaces(res.data);
        } catch (err) {
          setError('Failed to load interfaces');
        }
      };
      fetchSingle();
    }
  }, [isOpen, reflectorId, clusterReflectors, context]); // Remove getAccessTokenSilently from dependencies */

  // Get all IP addresses from all interfaces
  const allIpAddresses = interfaces.flatMap(iface => 
    iface.interfaceAddresses.map(addr => ({
      id: addr.id,
      ipAddress: addr.ipAddress
    }))
  );

  const handleInterfaceSelect = (ipAddress: string) => {
    const selectedAddress = allIpAddresses.find(addr => addr.ipAddress === ipAddress);
    if (selectedAddress) {
      setSelectedInterfaceId(selectedAddress.id);
      setLocalAddress(ipAddress);
    }
  };

  // Populate form fields when editing
  useEffect(() => {
    if (mode === "edit" && initialValues) {
      setNeighborAddress(initialValues.neighborAddress || '');
      setLocalAddress(initialValues.localAddress || '');
      setPeerAs(initialValues.peerAs?.toString() || '');
      setLocalAs(initialValues.localAs?.toString() || '');
      setDescription(initialValues.description || '');
      setOperatingMode((initialValues.operatingMode || 'enterprise').toLowerCase());
      setHoldTime(initialValues.holdTime?.toString() || '90');
      setKeepaliveInterval(initialValues.keepaliveInterval?.toString() || '30');
      setConnectRetry(initialValues.connectRetry?.toString() || '120');
      setEnableGracefulRestart(initialValues.enableGracefulRestart ?? true);
      setEnableAddPath(initialValues.enableAddPath ?? false);
      setEnableBfd(initialValues.enableBfd ?? true);
      setBfdInterval(initialValues.bfdInterval?.toString() || '300');
      setBfdMultiplier(initialValues.bfdMultiplier?.toString() || '3');
      setMd5Password(initialValues.md5Password || '');
    } else if (mode === "add") {
      // Optionally reset fields on add
      setNeighborAddress('');
      setLocalAddress('');
      setPeerAs('');
      setLocalAs('');
      setDescription('');
      setOperatingMode('enterprise');
      setHoldTime('90');
      setKeepaliveInterval('30');
      setConnectRetry('120');
      setEnableGracefulRestart(true);
      setEnableAddPath(false);
      setEnableBfd(true);
      setBfdInterval('300');
      setBfdMultiplier('3');
      setMd5Password('');
    }
  }, [mode, initialValues, isOpen]);

  const handleSubmit = async () => {
    const contextType = context || 'reflector';
    const isEditMode = mode === "edit" && initialValues && initialValues.id;

    // Build the base config
    const baseConfig = {
      neighborAddress,
      interfaceAddressId: null, // Set to null since we're not using local addresses
      peerAs: Number(peerAs),
      localAs: Number(localAs),
      description: description || '',
      operatingMode: operatingMode.toUpperCase(),
      holdTime: Number(holdTime),
      keepAliveInterval: Number(keepaliveInterval),
      connectRetry: Number(connectRetry),
      gracefulRestart: enableGracefulRestart,
      addPath: enableAddPath,
      BFD: enableBfd,
      bfdInterval: Number(bfdInterval),
      bfdMultiplier: Number(bfdMultiplier),
      password: md5Password || null,
    };

    let payload;
    let targetId;

    if (
      contextType === 'cluster' &&
      clusterReflectors &&
      clusterReflectors.length === 2
      // && selectedInterfaceId1 && selectedInterfaceId2 // COMMENTED OUT - no longer sending local addresses
    ) {
      // Cluster: send array for ctxr_config
      payload = {
        base_config: baseConfig,
        ctxr_config: [
          // { reflectorInstanceId: clusterReflectors[0], interfaceAddressId: selectedInterfaceId1 }, // COMMENTED OUT
          // { reflectorInstanceId: clusterReflectors[1], interfaceAddressId: selectedInterfaceId2 } // COMMENTED OUT
          { 
            reflectorInstanceId: clusterReflectors[0], 
            bgpNeighborId: isEditMode ? initialValues.id : null, // Include BGP neighbor ID for updates
            interfaceAddressId: null 
          }, // Set to null since we're not using local addresses
          { 
            reflectorInstanceId: clusterReflectors[1], 
            bgpNeighborId: isEditMode ? initialValues.id : null, // Include BGP neighbor ID for updates
            interfaceAddressId: null 
          } // Set to null since we're not using local addresses
        ]
      };
      targetId = clusterId;
    } else {
      // Standalone: send single object
      payload = {
        base_config: baseConfig,
        ctxr_config: {
          reflectorInstanceId: reflectorId as number, // ensure number
          bgpNeighborId: isEditMode ? initialValues.id : undefined, // Include BGP neighbor ID for updates
          interfaceAddressId: null // Set to null since we're not using local addresses
        }
      };
      targetId = reflectorId as number;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      if(targetId){
        if (isEditMode) {
          // Update existing BGP neighbor
          await updateBgpNeighbor(token, targetId, payload as any);
          setAlertState({
            isOpen: true,
            title: "Success",
            description: "BGP neighbor has been updated successfully.",
            variant: "success"
          });
        } else {
          // Create new BGP neighbor
          await createBgpNeighbor(token, targetId, payload as any);
          setAlertState({
            isOpen: true,
            title: "Success",
            description: "BGP neighbor has been created successfully.",
            variant: "success"
          });
        }
      }
      onClose();
      if (onSubmit) {
         onSubmit(payload);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} BGP neighbor:`, error);
      setAlertState({
        isOpen: true,
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} BGP neighbor. Please try again.`,
        variant: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  const holdTimeNum = Number(holdTime) || 0;
  const recommendedKeepalive = Math.round(holdTimeNum / 3);
  const recommendedHold = 90;
  const recommendedConnectRetry = 120;

  if (loading) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]"
      >
        <h2 className="text-xl font-semibold mb-4 text-white">
          Processing request...
        </h2>
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500"
        />
      </div>
    );
  }

  return (
    <>
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        title={
          <span className="flex items-center gap-2 font-semibold">
            {mode === "edit" ? "Edit BGP neighbor" : "Add BGP neighbor"}
          </span>
        }
      >
        <div className="flex flex-col items-center p-6 pt-2 gap-4 min-h-[400px] text-body">
          {/* Tabs */}
          <div className="w-full ">
            <GenericTabs
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className='px-0'
            />
          </div>
          {/* Tab Content */}
          {activeTab === 'Basic' && (
            <div className="flex flex-col gap-6 w-full">
              <div className="grid grid-cols-1 gap-2">
                {/* Neighbor Address */}
                <div className="w-full">
                  <GenericInput
                    label="Neighbor address"
                    value={neighborAddress}
                    onChange={setNeighborAddress}
                    type="text"
                    required
                  />
                  <div className="text-xs text-semantic-text-secondary ">IP address of the BGP peer</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {/* Peer AS */}
                <div className="w-full">
                  <GenericInput
                    label="Peer AS"
                    value={peerAs}
                    onChange={setPeerAs}
                    type="text"
                    required
                  />
                  <div className="text-xs text-semantic-text-secondary">Autonomous System number of the peer</div>
                </div>
                {/* Local AS */}
                <div className="w-full">
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={setLocalAs}
                    type="text"
                    required
                  />
                  <div className="text-xs text-semantic-text-secondary ">Local Autonomous System number for this session</div>
                </div>
              </div>
              {/* Description */}
              <GenericInput
                label="Description"
                value={description}
                onChange={setDescription}
                type="text"
              />
              <div className="text-xs text-semantic-text-secondary ">Optional description for this BGP session</div>
            </div>
          )}
          {activeTab === 'Operating mode' && (
            <div className="w-full">
              <div className="font-semibold mb-4 text-semantic-text-primary">Select mode</div>
              <CustomRadioGroup
                name="operatingMode"
                options={OPERATING_MODE_OPTIONS}
                value={operatingMode}
                onChange={v => setOperatingMode(v as 'enterprise' | 'telco')}
              />
            </div>
          )}
          {activeTab === 'Timers' && (
            <div className="w-full flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {/* Hold time */}
                <div>
                  <label className="block font-semibold  text-semantic-text-primary mb-1 text-body">Hold time <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 pr-16 text-base  text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)]"
                      value={holdTime}
                      onChange={e => setHoldTime(e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2  text-semantic-text-secondary text-base select-none ">seconds</span>
                  </div>
                </div>
                {/* Keepalive interval */}
                <div>
                  <label className="block font-semibold  text-semantic-text-primary mb-1 text-body">Keepalive interval <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 pr-16 text-base  text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)]"
                      value={keepaliveInterval}
                      onChange={e => setKeepaliveInterval(e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2  text-semantic-text-secondary text-base select-none">seconds</span>
                  </div>
                </div>
                {/* Connect retry (full width) */}
                <div className="col-span-2 w-1/2">
                  <label className="block font-semibold  text-semantic-text-primary mb-1 text-body">Connect retry <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 pr-16 text-base  text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)]"
                      value={connectRetry}
                      onChange={e => setConnectRetry(e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2  text-semantic-text-secondary text-base select-none">seconds</span>
                  </div>
                </div>
              </div>
              {/* Recommended settings box */}
              <div className="rounded-md bg-semantic-bg-secondary-alt border-semantic-border-secondary px-4 py-3 text-semantic-text-secondary">
                <div className="font-semibold text-semantic-text-secondary mb-1">Recommended timer settings:</div>
                <div>Hold time: {recommendedHold} seconds.</div>
                <div>Keepalive interval: {recommendedKeepalive} seconds (1/3 of hold time).</div>
                <div>Connect retry: {recommendedConnectRetry} seconds</div>
              </div>
            </div>
          )}
          {activeTab === 'Advanced' && (
            <div className="w-full flex flex-col gap-6">
              {/* Checkboxes */}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableGracefulRestart}
                    onChange={() => setEnableGracefulRestart(v => !v)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className={enableGracefulRestart ? "text-semantic-text-primary" : "text-semantic-text-secondary"}>
                    Enable graceful restart
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableAddPath}
                    onChange={() => setEnableAddPath(v => !v)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded-sm border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className={enableAddPath ? "text-semantic-text-primary" : "text-semantic-text-secondary"}>
                    Enable add-path
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableBfd}
                    onChange={() => setEnableBfd(v => !v)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className={enableBfd ? "text-semantic-text-primary" : "text-semantic-text-secondary"}>
                    Enable BFD
                  </span>
                </label>
              </div>
              {/* BFD Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <label className="block font-semibold text-semantic-text-primary mb-1 text-body">BFD interval <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 pr-12 text-base text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)] text-body"
                      value={bfdInterval}
                      onChange={e => setBfdInterval(e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-semantic-text-secondary text-base select-none">ms</span>
                  </div>
                </div>
                <div>
                  <label className="block font-semibold text-semantic-text-primary mb-1 text-body">BFD detection multiplier <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 text-base text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)] text-body"
                      value={bfdMultiplier}
                      onChange={e => setBfdMultiplier(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {/* MD5 Password */}
              <div>
                <label className="block font-semibold text-semantic-text-primary mb-1 text-body">MD5 password</label>
                <input
                  type="password"
                  className="w-full rounded-md border border-[var(--color-semantic-border-secondary,#DCDDE0)] px-4 py-2 text-base text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-semantic-bg-brand-bold,#197278)] text-body"
                  value={md5Password}
                  onChange={e => setMd5Password(e.target.value)}
                  placeholder=""
                />
                <div className="text-xs text-semantic-text-secondary mt-1">Optional password</div>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-4 mt-6 w-full">
            <GenericButton label="Cancel" variant="secondary" onClick={onClose} disabled={isSubmitting} />
            <GenericButton
              label={isSubmitting ? "Saving..." : "Save"}
              variant="brand"
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </div>
      </GenericModal>

      <GenericAlert
        isOpen={alertState.isOpen}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alertState.title}
        description={alertState.description}
        confirmLabel="OK"
        variant={alertState.variant}
      />
    </>
  );
};

export default BgpModal;
